import { axiosGet, axiosPost, axiosDelete } from "./axiosService";
import { userSlice } from "@/redux/User";
import environment from "@/constants/Environment";
import { BotContext } from "@/pages/cbb-builder/store";
import { useContext } from "react";
const environmentData = environment();
/**
 * Fetches Google Picker accounts configurations.
 */
export async function getAccounts() {
  try {
    const response = await axiosGet("/integration/googlepicker/accounts");
    return response?.data?.data?.configs ?? null;
  } catch (err: any) {
    console.error("Error fetching Google Picker accounts:", err);
    return null;
  }
}
declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

let isAuthenticating = false;

/**
 * Unified Google Picker Login / Auth Function
 */
export async function unifiedGoogleAuth({
  clientId,
  redirectUri,
  configs,
  setOpenConfigModalId,
  onSuccess,
  fetchConfig,
  dispatch,
  queryClient,
  setConfigs,
  setSelectedConfig,
}: {
  clientId: string;
  redirectUri: string;
  configs?: any[];
  setOpenConfigModalId?: (id: string) => void;
  onSuccess?: (data: any) => void;
  fetchConfig?: () => void;
  dispatch?: any;
  queryClient?: any;
  setConfigs?: (data: any) => void;
  setSelectedConfig?: (data: any) => void;
}) {
  if (isAuthenticating) return;
  isAuthenticating = true;

  try {
    const response = await axiosGet("/auth");
    const { codeChallenge } = response.data.data;
    if (!codeChallenge) throw new Error("Login canceled");

    const query = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      access_type: "offline",
      prompt: "consent",
      scope: [
        "https://www.googleapis.com/auth/drive.file",
        "https://www.googleapis.com/auth/userinfo.profile",
      ].join(" "),
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    });

    const authWindow = window.open(
      `https://accounts.google.com/o/oauth2/v2/auth?${query}`,
      "_blank",
      "width=600,height=600",
    );
    if (!authWindow) throw new Error("Login failed");

    return new Promise<void>((resolve, reject) => {
      const checkClosed = setInterval(async () => {
        console.log("close one");
        if (authWindow?.closed) {
          clearInterval(checkClosed);
          isAuthenticating = false;
          try {
            const configResponse = await axiosGet(
              "/configurations/emailConfigs?id=true&esp=googleapis",
            );
            const googleApis = await configResponse.data;

            if (googleApis[0]?._id) {
              const scopeValidation = await validateTokenScopes(
                googleApis[0]._id,
              );
              if (!scopeValidation.valid) {
                dispatch(
                  userSlice.actions.setToastPopUp({
                    show: true,
                    toastType: "error",
                    message:
                      "Missing required Google Drive permissions. Try again by providing the required permissions.",
                    duration: 4500,
                  }),
                );
                await axiosDelete(
                  `/integration/googlepicker/delete?_id=${googleApis[0]._id}`,
                );
                resolve();
                return;
              }
            }

            const existingConfig = configs?.some(
              (config) => config.id === googleApis[0]?.id,
            );
            setSelectedConfig?.(googleApis[0]);
            fetchConfig?.();

            if (setOpenConfigModalId && googleApis.length >= 1) {
              fetchConfig?.();
              setOpenConfigModalId("googlepicker");
            } else if (existingConfig) {
              // onSuccess?.(googleApis);
              fetchConfig?.();
              if (
                googleApis.length > 0 &&
                dispatch &&
                queryClient &&
                setSelectedConfig &&
                setConfigs
              ) {
                dispatch(
                  userSlice.actions.setToastPopUp({
                    show: true,
                    toastType: "success",
                    message: "Configuration added successfully",
                  }),
                );
                queryClient.invalidateQueries({
                  queryKey: ["googlePickerConfigs"],
                });
                setSelectedConfig(googleApis[0]);
                setConfigs(googleApis);
              }
            }
            resolve();
          } catch (error) {
            console.error("Error fetching configurations:", error);
            resolve();
          }
        }
      }, 500);
    });
  } catch (error) {
    isAuthenticating = false;
    console.error("Error during Google Picker login:", error);
    throw error;
  }
}

export async function extractTextFromPickerFile(
  json: any,
  _id: string,
  botData,
) {
  try {
    const response = await axiosPost("/integration/googlepicker/extract", {
      json,
      _id,
      botData,
    });
    return response?.data?.allFiles ?? null;
  } catch (error) {
    console.error("Error extracting text from Google Picker file:", error);
    return null;
  }
}

const loadGAPI = () => {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://apis.google.com/js/api.js";
    script.onload = () => resolve();
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

export const openPickerFrontend = async (_id: any, botData) => {
  const developerKey = encodeURIComponent(
    String(environmentData.GOOGLE_PICKER_DEVELOPER_KEY),
  );
  const clientId = encodeURIComponent(
    String(environmentData.PUBLIC_GOOGLE_CLIENT_ID),
  );
  const response = await axiosPost("/integration/googlepicker/open-picker", {
    _id,
  });

  return new Promise((resolve, reject) => {
    loadGAPI()
      .then(() => {
        window.gapi.load("picker", () => {
          const docsView = new window.google.picker.DocsView(
            window.google.picker.ViewId.DOCS,
          ).setMimeTypes(
            "application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/vnd.google-apps.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.google-apps.spreadsheet,application/vnd.ms-excel,text/csv,application/vnd.oasis.opendocument.spreadsheet",
          );

          const picker = new window.google.picker.PickerBuilder()
            .setDeveloperKey(developerKey)
            .setAppId(clientId)
            .setOAuthToken(response.data.accessToken)
            .addView(docsView)
            .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
            .setCallback(async (data) => {
              if (
                data[window.google.picker.Response.ACTION] ===
                window.google.picker.Action.PICKED
              ) {
                const googleFiles: any = data.docs.map((doc: any) => ({
                  url: doc.url,
                  name: doc.name,
                  mimeType: doc.mimeType,
                  size: doc.sizeBytes || 10000,
                  id: doc.id,
                }));

                const pageContent = await extractTextFromPickerFile(
                  googleFiles,
                  _id,
                  botData,
                );
                resolve(pageContent);
              } else if (
                data[window.google.picker.Response.ACTION] ===
                window.google.picker.Action.CANCEL
              ) {
                resolve(null);
              }
            })
            .build();

          picker.setVisible(true);
        });
      })
      .catch((err) => {
        console.error("Failed to load GAPI:", err);
        reject(err);
      });
  });
};

// Add a function to validate token scopes
export async function validateTokenScopes(_id: string) {
  try {
    // Get the access token
    const response = await axiosPost("/integration/googlepicker/open-picker", {
      _id,
    });

    if (!response.data || !response.data.accessToken) {
      return {
        valid: false,
        error: "Missing access token",
      };
    }

    // Check token info to validate scopes
    const tokenInfo = await fetch(
      `https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${response.data.accessToken}`,
    );
    const tokenInfoData = await tokenInfo.json();

    // Define required scopes
    const requiredScopes = [
      "https://www.googleapis.com/auth/drive.file",
      "https://www.googleapis.com/auth/userinfo.profile",
    ];

    // Check if token has an error
    if (tokenInfoData.error) {
      return {
        valid: false,
        error: tokenInfoData.error_description || "Invalid token",
      };
    }

    // If scope is not present or doesn't contain all required scopes
    if (!tokenInfoData.scope) {
      return {
        valid: false,
        error: "No permissions granted",
      };
    }

    // Check if all required scopes are included
    const tokenScopes = tokenInfoData.scope.split(" ");
    const missingScopes = requiredScopes.filter(
      (scope) => !tokenScopes.includes(scope),
    );

    if (missingScopes.length > 0) {
      return {
        valid: false,
        error: `Missing required permissions: ${missingScopes.join(", ")}`,
        missingScopes,
      };
    }

    return {
      valid: true,
      scopes: tokenScopes,
    };
  } catch (error) {
    console.error("Error validating token scopes:", error);
    return {
      valid: false,
      error: "Failed to validate permissions",
    };
  }
}
